// Appels HTTP typés vers /api/reconciliation/* — parse CSV relevé + apply.
//
// Flow :
//   1. previewFromFile(file)                  → { rows[], warnings[] }
//   2. user associe à chaque ligne une facture (ou pas)
//   3. apply({ transactions, confirmations }) → Payments créés
//
// On passe par `api.postForm` pour l'upload parce que le backend attend du
// multipart — le navigateur fixe la boundary correctement si on ne touche
// pas à Content-Type.

import { api } from "./client";
import type { UUID } from "./types";

export type BankTransactionPublic = {
  row_id: number;
  date: string; // ISO YYYY-MM-DD
  label: string;
  amount: string; // Decimal sérialisé en string
  currency: string;
};

export type ReconciliationSuggestion = {
  invoice_id: UUID;
  invoice_reference: string;
  client_name: string;
  amount_ttc: string;
  remaining: string;
  currency: string;
  score: number;
  reason: string;
};

export type ReconciliationRow = {
  transaction: BankTransactionPublic;
  suggestions: ReconciliationSuggestion[];
};

export type ReconciliationPreview = {
  rows: ReconciliationRow[];
  skipped_rows: number;
  warnings: string[];
  detected_separator: string;
  detected_columns: Record<string, string>;
};

export type ReconciliationConfirmation = {
  row_id: number;
  invoice_id: UUID;
  amount?: string | null;
  note?: string | null;
};

export type ReconciliationApplyRequest = {
  transactions: BankTransactionPublic[];
  confirmations: ReconciliationConfirmation[];
};

export type AppliedPaymentPublic = {
  row_id: number;
  payment_id: UUID;
  invoice_id: UUID;
  invoice_reference: string;
  amount: string;
  new_invoice_status: string;
};

export type ReconciliationApplyResponse = {
  applied: AppliedPaymentPublic[];
  errors: string[];
};

export const reconciliationApi = {
  previewFromFile: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.postForm<ReconciliationPreview>(
      "/api/reconciliation/preview",
      form,
    );
  },

  apply: (payload: ReconciliationApplyRequest) =>
    api.post<ReconciliationApplyResponse>(
      "/api/reconciliation/apply",
      payload,
    ),
};
